/**
 * Owner-facing receipt for one press of Pause, Resume, Stop, a steering send,
 * an approval decision, or Check again.
 *
 * "Sent" is the press itself. Acknowledged, Done, and Refused come only from a
 * node instruction outcome, an approval decision, or a command delivery /
 * command-linked control row. A boolean RPC success is not completion.
 */

export interface ControlReceiptView {
  readonly sent: true;
  readonly acknowledged: boolean;
  readonly done: boolean;
  readonly refusedReason: string | null;
}

export function sentReceipt(): ControlReceiptView {
  return { sent: true, acknowledged: false, done: false, refusedReason: null };
}

export function receiptFromSubmitRefusal(reason: string): ControlReceiptView {
  return { sent: true, acknowledged: false, done: false, refusedReason: reason.trim() };
}

export function controlReceiptLabel(view: ControlReceiptView, action?: string): string {
  const parts: string[] = ['Sent'];
  if (view.acknowledged) parts.push('Acknowledged');
  if (view.done) parts.push('Done');
  if (!view.done && view.refusedReason !== null) {
    const reason = view.refusedReason.trim();
    parts.push(reason === '' ? 'Refused' : `Refused (${reason})`);
  }
  const body = parts.join(' · ');
  const name = action?.trim() ?? '';
  return name === '' ? body : `${name} · ${body}`;
}

/** What Brunel said one Main Command message became, when it said. */
export interface CommandRouting {
  readonly routedAs?: string | null;
  readonly jobWorkerNumber?: number | null;
}

/** Said under a message Brunel has not said anything more exact about. */
export const STEERING_MESSAGE = 'Steering message';

/**
 * Said while a message is only on its way: Brunel has not yet said whether it is a new job
 * or steers a live run, so it is not called either.
 */
export const SENT_MESSAGE = 'Message';

/**
 * The name a sent message's receipt goes by. A message Brunel planned as a job says so, and
 * for which Worker when it addressed one: "Worker 3, ..." with no live run is a new job for
 * Worker 3, and calling it steering is what made the owner send it twice. Anything Brunel did
 * not call a job keeps the name it had.
 */
export function commandReceiptAction(routing: CommandRouting | null | undefined): string {
  if (routing?.routedAs !== 'job') return STEERING_MESSAGE;
  const worker = routing.jobWorkerNumber;
  return typeof worker === 'number' && Number.isInteger(worker) && worker > 0 ? `New job for Worker ${worker}` : 'New job';
}

export function sameReceipt(left: ControlReceiptView, right: ControlReceiptView): boolean {
  return left.acknowledged === right.acknowledged
    && left.done === right.done
    && left.refusedReason === right.refusedReason;
}

/** Keep the further truthful phase. Done is not replaced by a later guess. */
export function advanceReceipt(current: ControlReceiptView, next: ControlReceiptView | null): ControlReceiptView {
  if (next === null) return current;
  if (current.done && !next.done) return current;
  if (next.done) return { sent: true, acknowledged: true, done: true, refusedReason: null };
  if (next.refusedReason !== null) {
    return {
      sent: true,
      acknowledged: current.acknowledged || next.acknowledged,
      done: false,
      refusedReason: next.refusedReason,
    };
  }
  if (current.refusedReason !== null) return current;
  return {
    sent: true,
    acknowledged: current.acknowledged || next.acknowledged,
    done: false,
    refusedReason: null,
  };
}

/**
 * A node instruction records acknowledgement and outcome together.
 * Deferred is acknowledged and still open. A blank refusal detail is not filled in.
 */
export function receiptFromNodeInstruction(outcome: string | null, detail: string | null): ControlReceiptView {
  if (outcome === 'done') return { sent: true, acknowledged: true, done: true, refusedReason: null };
  if (outcome === 'deferred') return { sent: true, acknowledged: true, done: false, refusedReason: null };
  if (outcome === 'refused') {
    return { sent: true, acknowledged: true, done: false, refusedReason: detail?.trim() ?? '' };
  }
  return sentReceipt();
}

export interface CheckAgainInstruction {
  readonly node_id: string;
  readonly runtime_key?: string | null;
  readonly adapter_key?: string | null;
  readonly issued_at: string;
  readonly outcome: string | null;
  readonly detail: string | null;
}

function issuedAfter(left: string, right: string): boolean {
  const leftMs = Date.parse(left);
  const rightMs = Date.parse(right);
  if (!Number.isNaN(leftMs) && !Number.isNaN(rightMs)) return leftMs > rightMs;
  return left > right;
}

export function latestCheckAgainInstruction(
  rows: readonly CheckAgainInstruction[],
  nodeId: string,
  runtimeKey: string,
): CheckAgainInstruction | null {
  const matches = rows.filter((row) => {
    const key = row.runtime_key ?? row.adapter_key ?? '';
    return row.node_id === nodeId && key === runtimeKey;
  });
  return matches.reduce<CheckAgainInstruction | null>((best, row) => {
    if (best === null || issuedAfter(row.issued_at, best.issued_at)) return row;
    return best;
  }, null);
}

/**
 * Follow the instruction this press queued. An older row is not this press.
 * `alreadyWaiting` follows the row that was already in flight. A submit refusal
 * is the RPC's own words and is not an acknowledgement.
 */
export function receiptForCheckAgain(input: {
  readonly baselineIssuedAt: string | null;
  readonly alreadyWaiting: boolean;
  readonly submitRefusal: string | null;
  readonly rows: readonly CheckAgainInstruction[];
  readonly nodeId: string;
  readonly runtimeKey: string;
}): ControlReceiptView {
  if (input.submitRefusal !== null) return receiptFromSubmitRefusal(input.submitRefusal);
  const latest = latestCheckAgainInstruction(input.rows, input.nodeId, input.runtimeKey);
  if (latest === null) return sentReceipt();
  const sameRow = input.baselineIssuedAt !== null && !issuedAfter(latest.issued_at, input.baselineIssuedAt);
  if (sameRow && !input.alreadyWaiting) return sentReceipt();
  return receiptFromNodeInstruction(latest.outcome, latest.detail);
}

/** Approve and deny both finish the submit. A missing decision stays unsent. */
export function receiptFromApprovalDecision(decision: 'approve' | 'deny' | null): ControlReceiptView | null {
  if (decision === null) return null;
  return { sent: true, acknowledged: true, done: true, refusedReason: null };
}

export function receiptFromApprovalRpc(value: unknown): ControlReceiptView | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const status = (value as { status?: unknown }).status;
  if (status === 'approved') return receiptFromApprovalDecision('approve');
  if (status === 'denied') return receiptFromApprovalDecision('deny');
  return null;
}

export interface CommandControlFact {
  readonly state: 'completed' | 'accepted' | 'clarification' | 'refused';
  readonly reason: string;
  readonly summary: string;
}

export function receiptFromOwnerCommandControl(control: CommandControlFact): ControlReceiptView {
  switch (control.state) {
    case 'completed':
      return { sent: true, acknowledged: true, done: true, refusedReason: null };
    case 'accepted':
    case 'clarification':
      return { sent: true, acknowledged: true, done: false, refusedReason: null };
    case 'refused': {
      const summary = control.summary.trim();
      const reason = control.reason.trim();
      return { sent: true, acknowledged: false, done: false, refusedReason: summary !== '' ? summary : reason };
    }
  }
}

const ACKNOWLEDGED_DELIVERY = new Set(['accepted', 'waiting', 'queued', 'busy', 'claimed']);

/** Words already stored on a failed delivery. A bare "Failed" adds no extra reason. */
export function failedDeliveryReason(text: string): string {
  const trimmed = text.trim();
  if (trimmed === '' || trimmed.toLowerCase() === 'failed') return '';
  return trimmed.replace(/^failed\s*(?:·|-)\s*/i, '').trim();
}

/**
 * Per-recipient delivery states already loaded for one command.
 * Offline, skipped, unavailable, unsupported, and not configured stay at Sent.
 */
export function receiptFromDeliveries(
  rows: readonly { readonly state: string; readonly reason: string | null }[],
): ControlReceiptView | null {
  let sawAck = false;
  let sawDone = false;
  let refusal: string | null = null;
  for (const row of rows) {
    const state = row.state.trim().toLowerCase().replace(/[ -]+/g, '_');
    if (state === 'replied' || state === 'reply') {
      sawDone = true;
      continue;
    }
    if (state === 'failed' || state === 'error') {
      refusal = row.reason?.trim() ?? '';
      continue;
    }
    if (ACKNOWLEDGED_DELIVERY.has(state)) sawAck = true;
  }
  if (sawDone) return { sent: true, acknowledged: true, done: true, refusedReason: null };
  if (refusal !== null) return { sent: true, acknowledged: false, done: false, refusedReason: refusal };
  if (sawAck) return { sent: true, acknowledged: true, done: false, refusedReason: null };
  return null;
}

/** Command-linked control row and delivery rows already read for one send. */
export function receiptForCommandAction(input: {
  readonly control: CommandControlFact | null;
  readonly deliveries: readonly { readonly state: string; readonly reason: string | null }[];
}): ControlReceiptView | null {
  const fromControl = input.control === null ? null : receiptFromOwnerCommandControl(input.control);
  if (input.control?.state === 'refused' && fromControl !== null) return fromControl;
  const fromDelivery = receiptFromDeliveries(input.deliveries);
  if (fromControl === null) return fromDelivery;
  if (fromDelivery === null) return fromControl;
  if (fromControl.done) return fromControl;
  if (fromDelivery.done) return fromDelivery;
  if (fromDelivery.refusedReason !== null) return fromDelivery;
  if (fromControl.acknowledged || fromDelivery.acknowledged) {
    return { sent: true, acknowledged: true, done: false, refusedReason: null };
  }
  return fromControl;
}
